import type { UmbTreeElement } from '../../../tree.element.js';
import type { UmbDuplicateToModalData, UmbDuplicateToModalValue } from './duplicate-to-modal.token.js';
import { html, customElement, nothing, state } from '@umbraco-cms/backoffice/external/lit';
import { UmbTextStyles } from '@umbraco-cms/backoffice/style';
import { UmbModalBaseElement } from '@umbraco-cms/backoffice/modal';
import { UmbDeprecation } from '@umbraco-cms/backoffice/utils';
import type { UmbSelectionChangeEvent } from '@umbraco-cms/backoffice/event';

/**
 * @deprecated Deprecated since v17. The "Duplicate to" entity action now uses the shared `UMB_TREE_PICKER_MODAL`. Scheduled for removal in Umbraco 19.
 */
@customElement('umb-duplicate-to-modal')
export class UmbDuplicateToModalElement extends UmbModalBaseElement<UmbDuplicateToModalData, UmbDuplicateToModalValue> {
	@state()
	private _destinationUnique?: string | null;

	constructor() {
		super();
		new UmbDeprecation({
			deprecated: 'UmbDuplicateToModalElement (umb-duplicate-to-modal / Umb.Modal.DuplicateTo)',
			removeInVersion: '19.0.0',
			solution: 'Use the shared UMB_TREE_PICKER_MODAL instead, passing `headline` and `confirmLabel`.',
		}).warn();
	}

	private get _treeExpansion() {
		return this.data?.treeExpansion ?? [];
	}

	#onTreeSelectionChange(event: UmbSelectionChangeEvent) {
		const target = event.target as UmbTreeElement;
		const selection = target.getSelection();
		this._destinationUnique = selection[0];

		if (this._destinationUnique || this._destinationUnique === null) {
			this.updateValue({ destination: { unique: this._destinationUnique } });
		}
	}

	override render() {
		if (!this.data) return nothing;

		return html`
			<umb-body-layout headline=${this.localize.term('actions_copyTo')}>
				<umb-tree
					alias=${this.data.treeAlias}
					.props=${{
						foldersOnly: this.data?.foldersOnly,
						expandTreeRoot: true,
						expansion: this._treeExpansion,
					}}
					@selection-change=${this.#onTreeSelectionChange}></umb-tree>
				${this.#renderActions()}
			</umb-body-layout>
		`;
	}

	#renderActions() {
		return html`
			<uui-button
				slot="actions"
				label=${this.localize.term('general_cancel')}
				@click="${this._rejectModal}"></uui-button>
			<uui-button
				slot="actions"
				color="positive"
				look="primary"
				label=${this.localize.term('general_copy')}
				@click=${this._submitModal}
				?disabled=${this._destinationUnique === undefined}></uui-button>
		`;
	}

	static override styles = [UmbTextStyles];
}

export { UmbDuplicateToModalElement as element };

declare global {
	interface HTMLElementTagNameMap {
		'umb-duplicate-to-modal': UmbDuplicateToModalElement;
	}
}
